import Phaser from 'phaser';
import { sound } from '../../services/audioService.js';
import { ensureTextures } from '../textureGenerator.js';

export class FoodGame extends Phaser.Scene {
  constructor() {
    super({ key: 'FoodGame' });
  }

  init(data) {
    this.difficulty = data.difficulty || 'medium';
    this.barsToAdd = data.barsToAdd || (this.difficulty === 'hard' ? 9 : (this.difficulty === 'medium' ? 6 : 3));
    this.onComplete = data.onComplete;
    this.score = 0;
    this.lives = this.difficulty === 'hard' ? 2 : 3;

    // Difficulty settings
    if (this.difficulty === 'easy') {
      this.targetScore = 10;
      this.spawnInterval = 900;
      this.fallSpeed = 160;
      this.bombChance = 0.15;
    } else if (this.difficulty === 'medium') {
      this.targetScore = 15;
      this.spawnInterval = 750;
      this.fallSpeed = 220;
      this.bombChance = 0.28;
    } else {
      this.targetScore = 20;
      this.spawnInterval = 550;
      this.fallSpeed = 290;
      this.bombChance = 0.40;
    }
  }

  create() {
    ensureTextures(this);
    const { width, height } = this.cameras.main;
    this.isGameOver = false;

    // Background
    this.add.rectangle(width / 2, height / 2, width, height, 0x1A1B2F);

    // Catcher Tray
    this.tray = this.physics.add.sprite(width / 2, height - 70, 'catcher_tray');
    this.tray.setCollideWorldBounds(true);
    this.tray.body.setImmovable(true);

    // Items Group
    this.itemsGroup = this.physics.add.group();

    // Drag / Touch Controls (works instantly with single taps or smooth dragging)
    const handlePointer = (pointer) => {
      if (this.isGameOver) return;
      this.tray.x = Phaser.Math.Clamp(pointer.x, 40, width - 40);
    };
    this.input.on('pointermove', handlePointer);
    this.input.on('pointerdown', handlePointer);

    // Overlap detection
    this.physics.add.overlap(this.tray, this.itemsGroup, (tray, item) => {
      this.collectItem(item);
    });

    // HUD Text
    this.scoreText = this.add.text(16, 16, `Treats: ${this.score}/${this.targetScore}`, {
      fontSize: '15px',
      fontStyle: 'bold',
      color: '#FFFFFF'
    });

    this.livesText = this.add.text(width - 16, 16, `❤️ ${this.lives}`, {
      fontSize: '15px',
      fontStyle: 'bold',
      color: '#FF4081'
    }).setOrigin(1, 0);

    // Spawn Timer
    this.spawnTimer = this.time.addEvent({
      delay: this.spawnInterval,
      callback: this.spawnFallingItem,
      callbackScope: this,
      loop: true
    });
  }

  spawnFallingItem() {
    if (this.isGameOver) return;
    const { width } = this.cameras.main;
    const x = Phaser.Math.Between(30, width - 30);
    const isBomb = Math.random() < this.bombChance;

    let texture = 'food_apple';
    if (isBomb) {
      texture = 'hazard_bomb';
    } else {
      const foods = ['food_apple', 'food_donut', 'food_burger'];
      texture = Phaser.Utils.Array.GetRandom(foods);
    }

    const item = this.itemsGroup.create(x, -20, texture);
    item.setVelocityY(this.fallSpeed + Phaser.Math.Between(-20, 40));
    item.setData('isBomb', isBomb);
  }

  collectItem(item) {
    const isBomb = item.getData('isBomb');
    item.destroy();

    if (isBomb) {
      sound.playHit();
      this.lives--;
      this.livesText.setText(`❤️ ${this.lives}`);
      this.cameras.main.shake(150, 0.015);

      if (this.lives <= 0) {
        this.triggerGameOver(false);
      }
    } else {
      sound.playCatch();
      this.score++;
      this.scoreText.setText(`Treats: ${this.score}/${this.targetScore}`);

      // Visual punch
      this.tweens.add({
        targets: this.tray,
        scaleY: 0.8,
        duration: 60,
        yoyo: true
      });

      if (this.score >= this.targetScore) {
        this.triggerGameOver(true);
      }
    }
  }

  update() {
    const { height } = this.cameras.main;
    this.itemsGroup.children.iterate((item) => {
      if (item && item.y > height + 30) {
        item.destroy();
      }
    });
  }

  triggerGameOver(isWin) {
    this.isGameOver = true;
    this.spawnTimer.remove();
    this.itemsGroup.clear(true, true);

    const { width, height } = this.cameras.main;
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.75);

    if (isWin) {
      sound.playVictory();
      this.add.text(width / 2, height / 2 - 50, '🎉 VICTORY!', {
        fontSize: '26px',
        fontStyle: '800',
        color: '#00E676'
      }).setOrigin(0.5);

      this.add.text(width / 2, height / 2 - 10, `+${this.barsToAdd} FOOD BARS GAINED! 🍔`, {
        fontSize: '15px',
        fontStyle: 'bold',
        color: '#00E676'
      }).setOrigin(0.5);

      const finishBtn = this.add.rectangle(width / 2, height / 2 + 50, 140, 42, 0x00E676)
        .setInteractive({ cursor: 'pointer' });
      this.add.text(width / 2, height / 2 + 50, 'CLAIM & EXIT', {
        fontSize: '14px',
        fontStyle: 'bold',
        color: '#000'
      }).setOrigin(0.5);

      finishBtn.on('pointerdown', () => {
        sound.playClick();
        if (this.onComplete) this.onComplete(true);
      });
    } else {
      sound.playGameOver();
      this.add.text(width / 2, height / 2 - 40, '💥 OUCH!', {
        fontSize: '24px',
        fontStyle: '800',
        color: '#FF5252'
      }).setOrigin(0.5);

      this.add.text(width / 2, height / 2 - 5, 'Too many bombs hit!', {
        fontSize: '13px',
        color: '#A0A5BA'
      }).setOrigin(0.5);

      const retryBtn = this.add.rectangle(width / 2, height / 2 + 45, 120, 38, 0x6C5CE7)
        .setInteractive({ cursor: 'pointer' });
      this.add.text(width / 2, height / 2 + 45, 'RETRY', {
        fontSize: '13px',
        fontStyle: 'bold',
        color: '#FFF'
      }).setOrigin(0.5);

      retryBtn.on('pointerdown', () => {
        sound.playClick();
        this.scene.restart();
      });
    }
  }
}
